import React, { useRef, useCallback } from 'react';

interface CustomerAgreementProps {
  onScrolledToBottom?: () => void;
}

const CustomerAgreement: React.FC<CustomerAgreementProps> = ({ onScrolledToBottom }) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    if (!scrollRef.current || !onScrolledToBottom) return;
    const el = scrollRef.current;
    const atBottom = el.scrollHeight - el.scrollTop <= el.clientHeight + 50;
    if (atBottom) {
      onScrolledToBottom();
    }
  }, [onScrolledToBottom]);

  return (
    <div
      ref={scrollRef}
      className="bg-white dark:bg-gray-800 shadow-lg rounded-lg p-6 max-h-[600px] overflow-y-auto border border-gray-200 dark:border-gray-700"
      onScroll={handleScroll}
    >
      <div className="prose prose-sm dark:prose-invert max-w-none text-gray-900 dark:text-gray-300">
        <h2>1. Acceptance</h2>
        <p>By clicking &quot;I Agree,&quot; creating an account, or accessing the LENS™ platform (the &quot;Service&quot;), you (&quot;Customer&quot;) agree to be bound by this LENS™ Click-Thru Customer Agreement (&quot;Agreement&quot;) with Aivilo Cloud Technologies Inc., a Delaware company (&quot;Aivilo&quot;).</p>
        <p>If you do not agree, do not use the Service.</p>

        <h2>2. Scope of the Agreement</h2>
        <p>This Agreement governs Customer&apos;s access to and use of LENS™, Aivilo&apos;s SaaS platform that ingests cloud-environment metrics and delivers analytics, optimization recommendations, and automation workflows.</p>

        <h2>3. Account Registration &amp; Security</h2>
        <p>Customer must register an account with accurate information and is solely responsible for all activity under its account, maintaining the confidentiality of login credentials, and promptly notifying Aivilo of any unauthorized use.</p>

        <h2>4. Trial Use (60-Day Trial)</h2>
        <p>The Service is provided solely for Customer&apos;s internal, non-production evaluation during a sixty (60) day trial period. During the Trial Period, Customer may not use the Service in production workloads, process live customer or end-user data, or provide third-party services using the Service.</p>
        <p><strong>Automatic Expiration.</strong> Unless Customer enters into a paid subscription prior to the end of the Trial Period, Customer&apos;s access will automatically expire at the end of the Trial Period, without notice or further action.</p>

        <h2>5. Term and Termination</h2>
        <p>Either Party may terminate at any time, with or without cause. Upon termination, Customer must immediately cease all use of the Service.</p>

        <h2>6. Customer Data</h2>
        <p>Customer grants Aivilo a worldwide, royalty-free, non-exclusive license to ingest, process, store, and display Customer Data solely to provide and improve the Service. Customer shall not transmit personal data, PHI, PCI, or other sensitive data to the Service.</p>

        <h2>7. Confidentiality</h2>
        <p>Each Party shall use the other Party&apos;s Confidential Information solely to perform under this Agreement and protect it with at least the same degree of care it uses for its own similar information.</p>

        <h2>8. Intellectual Property</h2>
        <p>Aivilo and its licensors retain all right, title, and interest in the Service, software, documentation, and any derivatives. Customer retains all rights in Customer Data.</p>

        <h2>9. Restrictions</h2>
        <p>Customer shall not copy, modify, reverse-engineer, resell, sublicense, or use the Service in violation of law or to infringe third-party rights.</p>

        <h2>10. Disclaimers</h2>
        <p><strong>THE SERVICE IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE.&quot; AIVILO DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED.</strong></p>

        <h2>11. Limitation of Liability</h2>
        <p><strong>AIVILO&apos;S AGGREGATE LIABILITY SHALL NOT EXCEED THE GREATER OF ONE HUNDRED U.S. DOLLARS ($100) OR AMOUNTS PAID BY CUSTOMER IN THE TWELVE (12) MONTHS PRIOR TO THE CLAIM.</strong></p>

        <h2>12. Governing Law</h2>
        <p>This Agreement is governed by the laws of the State of New York. Exclusive jurisdiction and venue for any dispute shall be the state and federal courts in New York City.</p>

        <p className="mt-6 font-semibold">By clicking &quot;I Agree,&quot; Customer confirms it has read, understood, and agrees to be bound by this Agreement.</p>
      </div>
    </div>
  );
};

export default CustomerAgreement;
